import { FunctionComponent, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import {
    getDebouncedServiceOrderSearch,
    getServiceOrderCategory,
    getServiceOrderModalFilters,
    getServiceOrderSearchMode,
    getServiceOrderStatusFilter,
    serviceOrdersSlice,
} from '../../../reducers/serviceOrdersSlice';
import { BulkSelectionDropdown } from '../../../sharedComponents/table/BulkSelectionDropdown';
import { FilterMaterializationExceededErrorResponseCodec } from '../../../api/types/compoundManagement/errorCodes';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import { useGetServiceOrderIdsMutation } from '../../../api/serviceOrdersApi';
import { mapServiceOrderFiltersToQueryParams, mapServiceOrderSearchToQueryParams } from '../../shared/filter.types';

export const FIXED_SERVICE_ORDER_IDS_CACHE_KEY = 'shared-get-service-order-ids';
export const SERVICE_ORDER_MAX_SELECTION_SIZE = 100;

export const ServiceOrderBulkSelectionDropdown: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const { getIdsToUpdateSelection, hasTooManyFilterResultsError, resetTooManyFilterResultsError } =
        useGetServiceOrderIds();

    const handleAddFilterResultToSelection = async () => {
        const ids = await getIdsToUpdateSelection();
        if (ids !== undefined) {
            dispatch(serviceOrdersSlice.actions.addToBulkSelection(ids));
        }
    };

    const handleRemoveFilterResultFromSelection = async () => {
        const ids = await getIdsToUpdateSelection();
        if (ids !== undefined) {
            dispatch(serviceOrdersSlice.actions.removeFromBulkSelection(ids));
        }
    };

    const handleClearSelection = () => {
        dispatch(serviceOrdersSlice.actions.clearBulkSelection());
    };
    return (
        <BulkSelectionDropdown
            addFilterResultToSelection={handleAddFilterResultToSelection}
            removeFilterResultFromSelection={handleRemoveFilterResultFromSelection}
            clearSelection={handleClearSelection}
            hasTooManyFilterResultsError={hasTooManyFilterResultsError}
            maxSelectionSize={SERVICE_ORDER_MAX_SELECTION_SIZE}
            resetTooManyFilterResultsError={resetTooManyFilterResultsError}
        />
    );
};

const useGetServiceOrderIds = () => {
    const modalFilters = useAppSelector(getServiceOrderModalFilters);
    const statusFilter = useAppSelector(getServiceOrderStatusFilter);
    const searchMode = useAppSelector(getServiceOrderSearchMode);
    const searchValue = useAppSelector(getDebouncedServiceOrderSearch);
    const category = useAppSelector(getServiceOrderCategory);

    const [getServiceOrderIds] = useGetServiceOrderIdsMutation({
        fixedCacheKey: FIXED_SERVICE_ORDER_IDS_CACHE_KEY,
    });
    const [hasTooManyFilterResultsError, setHasTooManyFilterResultsError] = useState(false);

    const getIdsToUpdateSelection = async (): Promise<string[] | undefined> => {
        try {
            const filterParams = {
                ...mapServiceOrderFiltersToQueryParams(modalFilters, statusFilter, category),
                ...mapServiceOrderSearchToQueryParams(searchMode, searchValue),
            };
            return await getServiceOrderIds(filterParams).unwrap();
        } catch (error) {
            if (FilterMaterializationExceededErrorResponseCodec.is(error)) {
                setHasTooManyFilterResultsError(true);
            } else {
                handleQueryError(error);
            }
        }
    };

    const resetTooManyFilterResultsError = () => setHasTooManyFilterResultsError(false);

    return { getIdsToUpdateSelection, hasTooManyFilterResultsError, resetTooManyFilterResultsError };
};
